import React from 'react';
import { useField } from 'formik';
import Text from '@service-victoria/component-library/components/Text';
import TextArea from '@service-victoria/component-library/components/TextArea';
import RadioButton, {
  ItemType as RadiobuttonItem
} from '@service-victoria/component-library/components/RadioButton';
import * as Styled from '../../../styled';
import { DriverLicenceSelector } from '../DriverLicenceSelector';
import { isReturnExemption } from '../../travel-reasons';

const yesNoCheckbox: RadiobuttonItem[] = [
  {
    label: 'Yes',
    value: 'yes',
    inputProps: {
      'data-testid': 'NegativeYes-RadioButton'
    }
  },
  {
    label: 'No',
    value: 'no',
    inputProps: {
      'data-testid': 'NegativeNo-RadioButton'
    }
  }
];

const returningExemptionReasons: RadiobuttonItem[] = [
  'reuniting children with their parents, carers or families',
  'relocation due to serious housing insecurity',
  'medical reasons including mental health',
  'relocation for economic/work purposes',
  'schooling',
  'all other Victorians who wish to return'
].map(value => ({ label: value, value }));

const ExemptionForm: React.FC<Record<string, never>> = React.memo(function ExemptionForm() {
  const [{ value: travelReason }] = useField<string>('assess.reasons');

  const returnExemption = isReturnExemption(travelReason);

  return (
    <>
      {returnExemption ? (
        <>
          <Text.Heading.H3 fontWeight={600} id='assess.returningReason.label'>
            Tell us why you need a returning home exemption
          </Text.Heading.H3>
          <Text.Body>
            Exemptions are prioritised for consideration for one of the reasons below:
          </Text.Body>
          <Styled.RadioContainer>
            <RadioButton
              groupLabel=''
              items={returningExemptionReasons}
              name='assess.returningReason'
              aria-labelledby='assess.returningReason.label'
            />
          </Styled.RadioContainer>
        </>
      ) : (
        <>
          <Text.Heading.H3 fontWeight={600} id='assess.circumstances.label'>
            Your circumstances
          </Text.Heading.H3>
          <Text.Body>
            Please give specific details about why you need an exemption. This helps the Victorian
            Department of Health assess your special circumstances and grant exemptions.
          </Text.Body>
          <Styled.TextArea4LinesWrapper>
            <TextArea
              name='assess.circumstances'
              label=''
              hint='Limit: 300 words'
              maxLength={3000}
              aria-labelledby='assess.circumstances.label'
              data-testid='Circumstances-Input'
              errorLabelProps={{
                'data-testid': 'Circumstances-InputError'
              }}
            />
          </Styled.TextArea4LinesWrapper>
        </>
      )}
      <Styled.Spacer32 />
      <Styled.RadioContainer>
        <DriverLicenceSelector />
      </Styled.RadioContainer>
      {!returnExemption && (
        <>
          <Styled.Spacer32 />
          <Styled.RadioContainer>
            <RadioButton
              groupLabel="I've had a negative COVID-19 test result in the last 3 days"
              items={yesNoCheckbox}
              name='assess.negativeTest'
              errorLabelProps={{
                'data-testid': 'NegativeTest-RadioButtonError'
              }}
            />
          </Styled.RadioContainer>
        </>
      )}
    </>
  );
});

export default ExemptionForm;
